import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CameraCapture from '../components/CameraCapture';
import { useExpenses } from '../contexts/ExpenseContext';
import { useMode } from '../contexts/ModeContext';
import { CATEGORIES, FAMILY_CATEGORIES, TRAVEL_SUBCATEGORIES, ExpenseCategory, FamilyCategory, TravelSubcategory, getLocalDate } from '../types';
import { jsPDF } from 'jspdf';
import Tesseract from 'tesseract.js';
import * as pdfjsLib from 'pdfjs-dist';
// @ts-ignore - Vite URL import for worker
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;
import { 
  CheckCircle, 
  Loader2,
  AlertCircle,
  ChevronLeft,
  Camera,
  ArrowRight,
  FileImage,
  FileText,
  Zap,
  Eye
} from 'lucide-react';

export default function Scanner() {
  const navigate = useNavigate();
  const { addExpense } = useExpenses();
  const { isFamily } = useMode();
  const visibleCategories = isFamily ? FAMILY_CATEGORIES : CATEGORIES;

  const [step, setStep] = useState<'camera' | 'details' | 'success'>('camera');
  const [capturedImage, setCapturedImage] = useState<{ blob: Blob; filename: string } | null>(null);
  const [category, setCategory] = useState<ExpenseCategory | FamilyCategory>(isFamily ? 'provisions' : 'travel');
  const [subcategory, setSubcategory] = useState<TravelSubcategory>('uber');
  useEffect(() => {
    if (isFamily && !FAMILY_CATEGORIES.some(c => c.value === category)) {
      setCategory('provisions');
    } else if (!isFamily && !CATEGORIES.some(c => c.value === category)) {
      setCategory('travel');
    }
  }, [isFamily, category]);
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(getLocalDate());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [scannedCount, setScannedCount] = useState(0);
  const [saveFormat, setSaveFormat] = useState<'image' | 'pdf'>('image');
  
  // Target tax year for imports
  const currentYear = new Date().getFullYear();
  const [targetYear, setTargetYear] = useState<number | 'auto'>('auto');
  const availableYears = Array.from({ length: currentYear - 2019 }, (_, i) => currentYear - i);
  
  // OCR state
  const [isOcrProcessing, setIsOcrProcessing] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [ocrText, setOcrText] = useState('');
  const [showOcrText, setShowOcrText] = useState(false);
  const [ocrFieldsFound, setOcrFieldsFound] = useState<string[]>([]);
  
  // Queue of files to process (for batch uploads)
  const [fileQueue, setFileQueue] = useState<File[]>([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  
  // Quick template selection
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);

  // Extract data from OCR text
  const extractDataFromText = (text: string) => {
    console.log('OCR Text:', text);
    
    // Extract amount - look for dollar amounts
    const amountPatterns = [
      /(?:total|amount|due|paid|charge)[:\s]*\$?\s*([\d,]+\.?\d{0,2})/i,
      /\$\s*([\d,]+\.\d{2})/,
      /([\d,]+\.\d{2})\s*(?:total|usd|dollars)/i,
    ];
    
    let extractedAmount = '';
    for (const pattern of amountPatterns) {
      const match = text.match(pattern);
      if (match) {
        const amt = parseFloat(match[1].replace(/,/g, ''));
        // Only accept reasonable amounts ($1 - $10000)
        if (amt >= 1 && amt <= 10000) {
          extractedAmount = amt.toFixed(2);
          console.log('OCR: Found amount:', extractedAmount);
          break;
        }
      }
    }
    
    // Extract date
    const datePatterns = [
      /(\d{1,2})\/(\d{1,2})\/(\d{2,4})/,  // MM/DD/YYYY or MM/DD/YY
      /(\d{1,2})-(\d{1,2})-(\d{2,4})/,    // MM-DD-YYYY
      /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\.?\s+(\d{1,2}),?\s+(\d{4})/i,
    ];
    
    const months: Record<string, string> = {
      'jan': '01', 'feb': '02', 'mar': '03', 'apr': '04',
      'may': '05', 'jun': '06', 'jul': '07', 'aug': '08',
      'sep': '09', 'oct': '10', 'nov': '11', 'dec': '12'
    };
    
    let extractedDate = '';
    for (const pattern of datePatterns) {
      const match = text.match(pattern);
      if (match) {
        if (match[1].length <= 2 && !isNaN(parseInt(match[1]))) {
          // Numeric date format
          let year = match[3];
          if (year.length === 2) year = '20' + year;
          extractedDate = `${year}-${match[1].padStart(2, '0')}-${match[2].padStart(2, '0')}`;
        } else {
          // Month name format
          const monthNum = months[match[1].toLowerCase().substring(0, 3)];
          if (monthNum) {
            extractedDate = `${match[3]}-${monthNum}-${match[2].padStart(2, '0')}`;
          }
        }
        console.log('OCR: Found date:', extractedDate);
        break;
      }
    }
    
    // Extract vendor/description
    const vendorPatterns = [
      // Common fuel stations
      /(PILOT|LOVES|LOVE'S|FLYING\s*J|SHELL|EXXON|CHEVRON|BP|MARATHON|SPEEDWAY|MURPHY|CIRCLE\s*K|7-ELEVEN|CASEY|KWIK|QUIKTRIP|QT|RACETRAC|CEFCO)/i,
      // Hotels
      /(HOLIDAY\s*INN|HAMPTON\s*INN|COMFORT\s*INN|BEST\s*WESTERN|DAYS\s*INN|SUPER\s*8|LA\s*QUINTA|QUALITY\s*INN|MOTEL|HOTEL|INN)/i,
      // Rideshare
      /(UBER|LYFT)/i,
      // Car rental
      /(BUDGET|NATIONAL|ENTERPRISE|HERTZ|AVIS|DOLLAR|ALAMO)/i,
      // Generic store name (first line that looks like a name)
      /^([A-Z][A-Z\s&']+)$/m,
    ];
    
    let extractedVendor = '';
    for (const pattern of vendorPatterns) {
      const match = text.match(pattern);
      if (match) {
        extractedVendor = match[1].trim();
        console.log('OCR: Found vendor:', extractedVendor);
        break;
      }
    }
    
    // Try to detect category from vendor
    let detectedCategory: ExpenseCategory = 'meals';  // Default fallback
    let detectedSubcategory: TravelSubcategory = 'other';
    
    const textLower = text.toLowerCase();
    if (textLower.includes('uber')) {
      detectedCategory = 'travel';
      detectedSubcategory = 'uber';
    } else if (textLower.includes('lyft')) {
      detectedCategory = 'travel';
      detectedSubcategory = 'lyft';
    } else if (textLower.match(/budget|national|enterprise|hertz|avis|rental/i)) {
      detectedCategory = 'travel';
      detectedSubcategory = 'rental';
    } else if (textLower.match(/hotel|inn|motel|lodge|suites/i)) {
      detectedCategory = 'travel';
      detectedSubcategory = 'hotel';
    } else if (textLower.match(/shuttle|transport/i)) {
      detectedCategory = 'travel';
      detectedSubcategory = 'shuttle';
    } else if (textLower.match(/pilot|loves|flying\s*j|shell|exxon|chevron|fuel|diesel|gas|gallon/i)) {
      detectedCategory = 'fuel';
    }
    
    return {
      amount: extractedAmount,
      date: extractedDate,
      vendor: extractedVendor,
      category: detectedCategory,
      subcategory: detectedSubcategory,
    };
  };

  // Extract text from PDF using PDF.js
  const extractPdfText = async (pdfBlob: Blob): Promise<string> => {
    try {
      const arrayBuffer = await pdfBlob.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      let fullText = '';
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items
          .map((item: any) => item.str)
          .join(' ');
        fullText += pageText + '\n';
      }
      
      console.log('PDF text extracted, length:', fullText.length);
      return fullText;
    } catch (err) {
      console.error('PDF extraction error:', err);
      return '';
    }
  };

  // Convert PDF page to image for OCR (for scanned PDFs that have no text)
  const pdfToImage = async (pdfBlob: Blob): Promise<Blob | null> => {
    try {
      console.log('Converting PDF to image, blob size:', pdfBlob.size);
      const arrayBuffer = await pdfBlob.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const page = await pdf.getPage(1); // Get first page
      
      // Render at 2x scale for better OCR accuracy
      const scale = 2.0;
      const viewport = page.getViewport({ scale });
      
      console.log('PDF page viewport:', viewport.width, 'x', viewport.height);
      
      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('Failed to get canvas context');
        return null;
      }
      
      await page.render({
        canvasContext: ctx,
        viewport: viewport
      }).promise;
      
      console.log('PDF rendered to canvas successfully');
      
      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          console.log('Canvas converted to blob:', blob?.size || 'null');
          resolve(blob);
        }, 'image/png');
      });
    } catch (err) {
      console.error('PDF to image conversion error:', err);
      return null;
    }
  };

  // Run OCR on image OR extract text from PDF
  const runOcr = async (fileBlob: Blob, filename: string) => {
    setIsOcrProcessing(true);
    setOcrProgress(0);
    setOcrText('');
    
    const isPdf = fileBlob.type === 'application/pdf' || filename.toLowerCase().endsWith('.pdf');
    
    try {
      let text = '';
      
      if (isPdf) {
        // First try to extract text from PDF
        setOcrProgress(20);
        console.log('Extracting text from PDF...');
        text = await extractPdfText(fileBlob);
        
        // Check if we got meaningful text (more than 20 chars of non-whitespace)
        const meaningfulText = text.replace(/\s+/g, '').length;
        console.log('PDF text extraction result:', meaningfulText, 'chars');
        
        if (meaningfulText < 20) {
          // This is likely a scanned image PDF - convert to image and run OCR
          console.log('PDF appears to be scanned image, converting to image for OCR...');
          setOcrProgress(30);
          
          const imageBlob = await pdfToImage(fileBlob);
          if (imageBlob) {
            console.log('Running Tesseract OCR on converted image...');
            const result = await Tesseract.recognize(
              imageBlob,
              'eng',
              {
                logger: (m) => {
                  if (m.status === 'recognizing text') {
                    setOcrProgress(30 + Math.round(m.progress * 70));
                  }
                }
              }
            );
            text = result.data.text;
            console.log('OCR from scanned PDF complete, text length:', text.length);
          } else {
            console.warn('Failed to convert PDF to image for OCR');
          }
        }
        setOcrProgress(100);
      } else {
        // Use Tesseract OCR for images
        console.log('Running Tesseract OCR on image...');
        const result = await Tesseract.recognize(
          fileBlob,
          'eng',
          {
            logger: (m) => {
              if (m.status === 'recognizing text') {
                setOcrProgress(Math.round(m.progress * 100));
              }
            }
          }
        );
        text = result.data.text;
      }
      
      setOcrText(text);
      console.log('Extracted text:', text.substring(0, 500));
      
      // Extract and auto-fill data
      const extracted = extractDataFromText(text);
      const fieldsFound: string[] = [];
      
      if (extracted.amount && !amount) {
        setAmount(extracted.amount);
        fieldsFound.push(`$${extracted.amount}`);
      }
      if (extracted.date) {
        setDate(extracted.date);
        fieldsFound.push(extracted.date);
      }
      if (extracted.vendor) {
        setDescription(extracted.vendor);
        fieldsFound.push(extracted.vendor);
      }
      if (extracted.category) {
        setCategory(extracted.category);
        fieldsFound.push(extracted.category);
        if (extracted.category === 'travel' && extracted.subcategory) {
          setSubcategory(extracted.subcategory);
        }
      }
      
      setOcrFieldsFound(fieldsFound);
      console.log('Extraction Complete:', extracted, 'Fields found:', fieldsFound);
    } catch (err) {
      console.error('Text extraction error:', err);
      setOcrFieldsFound([]);
      setOcrText('');
    } finally {
      setIsOcrProcessing(false);
      setOcrProgress(100);
    }
  };

  const handleCapture = (blob: Blob, filename: string) => {
    setCapturedImage({ blob, filename });
    setStep('details');
    // Auto-run text extraction (OCR for images, PDF.js for PDFs)
    runOcr(blob, filename);
  };

  const handleCancel = () => {
    navigate('/');
  };

  const scanAnother = () => {
    // Reset form but keep category for convenience
    setCapturedImage(null);
    setDescription('');
    setAmount('');
    setDate(getLocalDate());
    setError('');
    setOcrText('');
    setOcrProgress(0);
    setShowOcrText(false);
    setOcrFieldsFound([]);
    setStep('camera');
  };

  const finishScanning = () => {
    navigate('/expenses');
  };

  // Generate a readable filename
  const generateFilename = (ext: string) => {
    const cat = visibleCategories.find(c => c.value === category);
    const catName = cat?.label || category;
    const dateStr = date.replace(/-/g, '');
    const desc = description 
      ? '_' + description.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20)
      : '';
    return `${catName}${desc}_${dateStr}.${ext}`;
  };

  const convertToPdf = async (imageBlob: Blob): Promise<{ blob: Blob; filename: string }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          // Create PDF with image dimensions
          const orientation = img.width > img.height ? 'landscape' : 'portrait';
          const pdf = new jsPDF({
            orientation,
            unit: 'px',
            format: [img.width, img.height]
          });
          
          // Add image to PDF
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }
          ctx.drawImage(img, 0, 0);
          const imgData = canvas.toDataURL('image/jpeg', 0.9);
          
          pdf.addImage(imgData, 'JPEG', 0, 0, img.width, img.height);
          
          const pdfBlob = pdf.output('blob');
          resolve({
            blob: pdfBlob,
            filename: generateFilename('pdf')
          });
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = () => reject(new Error('Failed to load image for PDF conversion'));
      img.src = URL.createObjectURL(imageBlob);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!capturedImage) return;

    setIsSubmitting(true);
    setError('');

    try {
      let fileToSave: { blob: Blob; filename: string };
      
      // Check if the file is already a PDF
      const isAlreadyPdf = capturedImage.blob.type === 'application/pdf' || 
                          capturedImage.filename.toLowerCase().endsWith('.pdf');
      
      console.log('Saving expense:', {
        isAlreadyPdf,
        blobType: capturedImage.blob.type,
        blobSize: capturedImage.blob.size,
        filename: capturedImage.filename,
        category,
        subcategory,
        amount,
        date
      });
      
      if (isAlreadyPdf) {
        // Keep PDF as-is, just update filename if needed
        fileToSave = {
          blob: capturedImage.blob,
          filename: capturedImage.filename.endsWith('.pdf') 
            ? capturedImage.filename 
            : generateFilename('pdf')
        };
      } else if (saveFormat === 'pdf') {
        // Convert image to PDF
        fileToSave = await convertToPdf(capturedImage.blob);
      } else {
        // Keep as image
        fileToSave = {
          blob: capturedImage.blob,
          filename: generateFilename('jpg')
        };
      }

      console.log('File to save:', {
        filename: fileToSave.filename,
        size: fileToSave.blob.size,
        type: fileToSave.blob.type
      });

      // Determine the date to use - override year if targetYear is set
      let dateToUse = date;
      if (targetYear !== 'auto' && date) {
        // Replace the year portion of the date (YYYY-MM-DD format)
        const dateParts = date.split('-');
        if (dateParts.length === 3) {
          dateToUse = `${targetYear}-${dateParts[1]}-${dateParts[2]}`;
        }
      }

      await addExpense({
        category,
        subcategory: category === 'travel' ? subcategory : undefined,
        description,
        amount: amount ? parseFloat(amount) : undefined,
        date: dateToUse,
        file: fileToSave.blob,
        filename: fileToSave.filename,
      });

      setScannedCount(prev => prev + 1);
      setStep('success');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to save: ${errorMessage}`);
      console.error('Save error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle file upload from folder/scanner (multiple files)
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      setFileQueue(fileArray);
      setCurrentFileIndex(0);
      // Start with first file - File already extends Blob, use directly
      const firstFile = fileArray[0];
      handleCapture(firstFile, firstFile.name);
    }
  };

  // Process next file in queue
  const processNextFile = () => {
    const nextIndex = currentFileIndex + 1;
    if (nextIndex < fileQueue.length) {
      setCurrentFileIndex(nextIndex);
      const nextFile = fileQueue[nextIndex];
      // Reset form for next file
      setDescription('');
      setAmount('');
      setDate(getLocalDate());
      setError('');
      setOcrText('');
      setOcrProgress(0);
      setShowOcrText(false);
      setOcrFieldsFound([]);
      // File already extends Blob, use directly
      handleCapture(nextFile, nextFile.name);
    } else {
      // All files processed
      setFileQueue([]);
      setCurrentFileIndex(0);
      setStep('success');
    }
  };

  if (step === 'success') {
    return (
      <div className="max-w-md mx-auto text-center py-12 animate-fade-in">
        <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">
          Saved Successfully!
        </h2>
        <p className="text-white/60 mb-2">
          {scannedCount} receipt{scannedCount > 1 ? 's' : ''} saved this session
        </p>
        
        {/* Show queue progress if processing multiple files */}
        {fileQueue.length > 0 && currentFileIndex < fileQueue.length - 1 && (
          <p className="text-shuttle-sand font-medium mb-4">
            📁 {fileQueue.length - currentFileIndex - 1} more file{fileQueue.length - currentFileIndex - 1 > 1 ? 's' : ''} remaining
          </p>
        )}
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
          {fileQueue.length > 0 && currentFileIndex < fileQueue.length - 1 ? (
            <button
              onClick={processNextFile}
              className="flex items-center justify-center space-x-2 px-6 py-4 bg-gradient-to-r from-shuttle-sand to-amber-500 text-shuttle-dark rounded-xl font-medium hover:from-amber-400 hover:to-amber-500 transition-all"
            >
              <FileImage className="w-5 h-5" />
              <span>Next File ({currentFileIndex + 2}/{fileQueue.length})</span>
            </button>
          ) : (
            <button
              onClick={scanAnother}
              className="flex items-center justify-center space-x-2 px-6 py-4 bg-gradient-to-r from-shuttle-sand to-amber-500 text-shuttle-dark rounded-xl font-medium hover:from-amber-400 hover:to-amber-500 transition-all"
            >
              <Camera className="w-5 h-5" />
              <span>Add Another</span>
            </button>
          )}
          <button
            onClick={finishScanning}
            className="flex items-center justify-center space-x-2 px-6 py-4 bg-white/10 text-white border border-white/20 rounded-xl font-medium hover:bg-white/20 transition-all"
          >
            <span>Done</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
        
        <p className="text-white/40 text-sm mt-6">
          💡 Keep scanning to add more receipts
        </p>
      </div>
    );
  }

  // Quick templates for common receipt types
  const quickTemplates = [
    { icon: '⛽', label: 'Fuel', category: 'fuel' as ExpenseCategory, subcategory: undefined, color: 'orange' },
    { icon: '🏨', label: 'Hotel', category: 'travel' as ExpenseCategory, subcategory: 'hotel' as TravelSubcategory, color: 'purple' },
    { icon: '🚙', label: 'Car Rental', category: 'travel' as ExpenseCategory, subcategory: 'rental' as TravelSubcategory, color: 'blue' },
    { icon: '🚐', label: 'Shuttle', category: 'travel' as ExpenseCategory, subcategory: 'shuttle' as TravelSubcategory, color: 'green' },
    { icon: '🚗', label: 'Uber/Lyft', category: 'travel' as ExpenseCategory, subcategory: 'uber' as TravelSubcategory, color: 'slate' },
    { icon: '🍔', label: 'Meals', category: 'meals' as ExpenseCategory, subcategory: undefined, color: 'yellow' },
    { icon: '🅿️', label: 'Parking', category: 'travel' as ExpenseCategory, subcategory: 'parking' as TravelSubcategory, color: 'cyan' },
    { icon: '📋', label: 'Other', category: 'travel' as ExpenseCategory, subcategory: 'other' as TravelSubcategory, color: 'gray' },
  ];

  const applyTemplate = (template: typeof quickTemplates[0]) => {
    setCategory(template.category);
    if (template.subcategory) {
      setSubcategory(template.subcategory);
    }
    setSelectedTemplate(template.label);
  };

  if (step === 'camera') {
    return (
      <div className="max-w-2xl mx-auto animate-fade-in">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Add Receipt</h1>
          <p className="text-white/60 mt-1">Take a photo or upload from your computer</p>
        </div>

        {/* Quick Templates */}
        <div className="mb-6 p-4 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10">
          <h3 className="text-sm font-medium text-white/70 mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-shuttle-sand" />
            Quick Templates - Select before scanning:
          </h3>
          <div className="grid grid-cols-4 gap-2">
            {quickTemplates.map((template) => (
              <button
                key={template.label}
                type="button"
                onClick={() => applyTemplate(template)}
                className={`p-3 rounded-xl border-2 text-center transition-all ${
                  selectedTemplate === template.label
                    ? 'border-shuttle-sand bg-shuttle-sand/20 scale-105'
                    : 'border-white/20 hover:border-white/40 hover:bg-white/5'
                }`}
              >
                <span className="text-2xl block">{template.icon}</span>
                <span className="text-xs text-white/70 mt-1 block">{template.label}</span>
              </button>
            ))}
          </div>
          {selectedTemplate && (
            <p className="text-shuttle-sand text-sm mt-3 text-center">
              ✓ {selectedTemplate} template selected - uploads will be pre-categorized
            </p>
          )}
        </div>
        
        {/* Upload from folder option */}
        <div className="mb-6 p-6 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20">
          <label className="block cursor-pointer">
            <div className="flex flex-col items-center justify-center py-8 border-2 border-dashed border-white/30 rounded-xl hover:border-shuttle-sand hover:bg-white/5 transition-all">
              <FileImage className="w-12 h-12 text-shuttle-sand mb-4" />
              <span className="text-white font-medium mb-2">Upload from ScanSnap / Folder</span>
              <span className="text-white/60 text-sm">Click to select images or PDFs</span>
              <span className="text-shuttle-sand text-sm mt-1">📁 Select multiple files at once!</span>
              <span className="text-white/40 text-xs mt-2">Supports: JPG, PNG, PDF</span>
            </div>
            <input
              type="file"
              accept="image/*,.pdf"
              multiple
              onChange={handleFileUpload}
              className="hidden"
            />
          </label>
        </div>
        
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 h-px bg-white/20"></div>
          <span className="text-white/40 text-sm">OR</span>
          <div className="flex-1 h-px bg-white/20"></div>
        </div>
        
        <div className="mb-4">
          <h2 className="text-lg font-medium text-white mb-2 flex items-center gap-2">
            <Camera className="w-5 h-5" />
            Use Camera
          </h2>
        </div>
        
        <CameraCapture onCapture={handleCapture} onCancel={handleCancel} />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <button
        onClick={() => setStep('camera')}
        className="flex items-center space-x-2 text-white/70 hover:text-white mb-6 transition-colors"
      >
        <ChevronLeft className="w-5 h-5" />
        <span>Retake Photo</span>
      </button>

      <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden">
        {/* File Preview */}
        {capturedImage && (
          <div className="aspect-video bg-black flex items-center justify-center">
            {capturedImage.blob.type === 'application/pdf' || capturedImage.filename.toLowerCase().endsWith('.pdf') ? (
              // PDF Preview - show icon and filename
              <div className="text-center p-8">
                <FileText className="w-16 h-16 text-red-400 mx-auto mb-4" />
                <p className="text-white font-medium">{capturedImage.filename}</p>
                <p className="text-white/50 text-sm mt-1">PDF Document</p>
              </div>
            ) : (
              // Image Preview
              <img
                src={URL.createObjectURL(capturedImage.blob)}
                alt="Captured receipt"
                className="max-h-full max-w-full object-contain"
                onError={(e) => {
                  // If image fails to load, show placeholder
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.parentElement!.innerHTML = `
                    <div class="text-center p-8">
                      <svg class="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                      <p class="text-white/70">Image Preview</p>
                      <p class="text-white/40 text-sm">${capturedImage.filename}</p>
                    </div>
                  `;
                }}
              />
            )}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* OCR Status */}
          {isOcrProcessing && (
            <div className="flex items-center space-x-3 p-4 bg-blue-500/20 border border-blue-500/50 rounded-xl">
              <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
              <div className="flex-1">
                <p className="text-blue-300 text-sm font-medium">Reading receipt with OCR...</p>
                <div className="w-full bg-blue-900/50 rounded-full h-2 mt-2">
                  <div 
                    className="bg-blue-400 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${ocrProgress}%` }}
                  />
                </div>
              </div>
            </div>
          )}
          
          {/* OCR Success */}
          {!isOcrProcessing && ocrProgress === 100 && (
            <div className={`p-4 rounded-xl ${ocrFieldsFound.length > 0 ? 'bg-green-500/20 border border-green-500/50' : 'bg-amber-500/20 border border-amber-500/50'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Zap className={`w-5 h-5 ${ocrFieldsFound.length > 0 ? 'text-green-400' : 'text-amber-400'}`} />
                  <span className={`text-sm font-medium ${ocrFieldsFound.length > 0 ? 'text-green-300' : 'text-amber-300'}`}>
                    {ocrFieldsFound.length > 0 
                      ? `OCR Complete - Found: ${ocrFieldsFound.join(', ')}`
                      : 'OCR Complete - No data auto-detected (fill manually)'}
                  </span>
                </div>
                {ocrText && (
                  <button
                    type="button"
                    onClick={() => setShowOcrText(!showOcrText)}
                    className={`flex items-center space-x-1 text-sm ${ocrFieldsFound.length > 0 ? 'text-green-400 hover:text-green-300' : 'text-amber-400 hover:text-amber-300'}`}
                  >
                    <Eye className="w-4 h-4" />
                    <span>{showOcrText ? 'Hide' : 'View'} Text</span>
                  </button>
                )}
              </div>
              {showOcrText && ocrText && (
                <pre className="mt-3 p-3 bg-black/30 rounded-lg text-white/70 text-xs overflow-auto max-h-40 whitespace-pre-wrap">
                  {ocrText || '(No text extracted)'}
                </pre>
              )}
            </div>
          )}

          {error && (
            <div className="flex items-center space-x-2 p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-300 text-sm">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              Category
            </label>
            <div className="grid grid-cols-4 gap-2">
              {visibleCategories.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setCategory(cat.value)}
                  className={`p-3 rounded-xl border-2 text-center transition-all ${
                    category === cat.value
                      ? 'border-shuttle-sand bg-shuttle-sand/20'
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <span className="text-xl block">{cat.icon}</span>
                  <span className="text-xs text-white/70 mt-1 block truncate">{cat.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Travel Subcategory */}
          {category === 'travel' && (
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                Travel Type
              </label>
              <div className="grid grid-cols-4 gap-2">
                {TRAVEL_SUBCATEGORIES.map((sub) => (
                  <button
                    key={sub.value}
                    type="button"
                    onClick={() => setSubcategory(sub.value)}
                    className={`p-2 rounded-xl border-2 text-center transition-all ${
                      subcategory === sub.value
                        ? 'border-shuttle-sand bg-shuttle-sand/20'
                        : 'border-white/20 hover:border-white/40'
                    }`}
                  >
                    <span className="text-lg block">{sub.icon}</span>
                    <span className="text-xs text-white/70 truncate block">{sub.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-white/80 mb-1">
              Description
            </label>
            <input
              id="description"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Uber from airport to hotel"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
            />
          </div>

          {/* Target Tax Year */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3">
            <label className="block text-sm font-medium text-amber-300 mb-2">
              📅 Target Tax Year
            </label>
            <select
              value={targetYear}
              onChange={(e) => setTargetYear(e.target.value === 'auto' ? 'auto' : parseInt(e.target.value))}
              className="w-full px-4 py-2 bg-shuttle-dark border border-amber-500/50 rounded-xl text-white focus:ring-2 focus:ring-amber-500 transition-all"
            >
              <option value="auto">🔄 Auto-detect from date</option>
              {availableYears.map(year => (
                <option key={year} value={year}>
                  {year} {year < currentYear ? '(Back taxes)' : '(Current)'}
                </option>
              ))}
            </select>
            {targetYear !== 'auto' && (
              <p className="text-xs text-amber-200/70 mt-2">
                Receipt will be saved to tax year {targetYear}
              </p>
            )}
          </div>

          {/* Amount and Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="amount" className="block text-sm font-medium text-white/80 mb-1">
                Amount ($)
              </label>
              <input
                id="amount"
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
              />
            </div>
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-white/80 mb-1">
                Date
              </label>
              <input
                id="date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
              />
            </div>
          </div>

          {/* Save Format */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              Save As
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setSaveFormat('image')}
                className={`flex items-center justify-center space-x-2 p-3 rounded-xl border-2 transition-all ${
                  saveFormat === 'image'
                    ? 'border-shuttle-sand bg-shuttle-sand/20'
                    : 'border-white/20 hover:border-white/40'
                }`}
              >
                <FileImage className="w-5 h-5" />
                <span className="text-white">Image (JPG)</span>
              </button>
              <button
                type="button"
                onClick={() => setSaveFormat('pdf')}
                className={`flex items-center justify-center space-x-2 p-3 rounded-xl border-2 transition-all ${
                  saveFormat === 'pdf'
                    ? 'border-shuttle-sand bg-shuttle-sand/20'
                    : 'border-white/20 hover:border-white/40'
                }`}
              >
                <FileText className="w-5 h-5" />
                <span className="text-white">PDF</span>
              </button>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex items-center justify-center space-x-2 py-4 px-6 bg-gradient-to-r from-shuttle-sand to-amber-500 text-shuttle-dark rounded-xl font-medium hover:from-amber-400 hover:to-amber-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                <span>Save Expense</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}





